/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useEffect, useRef, useState } from "react";
import {
  Layout,
  Row,
  Col,
  Card,
  Typography,
  Statistic,
  Spin,
  Alert,
  Progress,
  List,
  Tag,
} from "antd";
import {
  BarChartOutlined,
  CloudOutlined,
  RiseOutlined,
  EnvironmentOutlined,
  ThunderboltOutlined,
  SyncOutlined,
  PoweroffOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  FireOutlined,
  DollarOutlined,
} from "@ant-design/icons";
import { useQuery } from "@apollo/client";
import {
  GET_CURRENT_ENERGY_DATA,
  GET_CURRENT_WEATHER_DATA,
} from "../../graphql/queries";
import { useAuth } from "../../contexts/AuthContext";
import { useLocation } from "../../contexts/LocationContext";

const { Content } = Layout;
const { Title, Text } = Typography;

// Scale.ai-inspired all-black theme
const THEME = {
  bg: "#000000",
  surface: "#0a0a0a",
  surfaceElevated: "#111111",
  border: "#1a1a1a",
  borderSubtle: "#0f0f0f",
  text: "#ffffff",
  textSecondary: "#888888",
  textTertiary: "#555555",
  accent: "#3b82f6",
  success: "#10b981",
  warning: "#f59e0b",
  danger: "#ef4444",
  purple: "#8b5cf6",
};

// Scroll animation hook
const useScrollReveal = () => {
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      {
        threshold: 0.1,
        rootMargin: "0px 0px -50px 0px",
      }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => observer.disconnect();
  }, []);

  return { ref, isVisible };
};

// Animated section wrapper
const AnimatedSection: React.FC<{ children: React.ReactNode; delay?: number }> = ({
  children,
  delay = 0,
}) => {
  const { ref, isVisible } = useScrollReveal();

  return (
    <div
      ref={ref}
      style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? "translateY(0)" : "translateY(30px)",
        transition: `all 0.6s cubic-bezier(0.16, 1, 0.3, 1) ${delay}ms`,
      }}
    >
      {children}
    </div>
  );
};

// Metric card with glow effect
const MetricCard: React.FC<{
  title: string;
  value: string | number;
  suffix?: string;
  prefix?: React.ReactNode;
  icon?: React.ReactNode;
  trend?: { value: number; isUp: boolean };
  color?: string;
}> = ({ title, value, suffix, prefix, icon, trend, color = THEME.accent }) => {
  return (
    <Card
      style={{
        background: THEME.surface,
        border: `1px solid ${THEME.border}`,
        borderRadius: "12px",
        height: "100%",
        transition: "all 0.3s ease",
        position: "relative",
        overflow: "hidden",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = color;
        e.currentTarget.style.boxShadow = `0 0 20px ${color}22`;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = THEME.border;
        e.currentTarget.style.boxShadow = "none";
      }}
    >
      <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: "2px", background: `linear-gradient(90deg, ${color}, transparent)` }} />
      <div style={{ padding: "8px 0" }}>
        <div style={{ display: "flex", alignItems: "center", marginBottom: "16px" }}>
          <div style={{ color, fontSize: "20px", marginRight: "8px" }}>{icon}</div>
          <Text style={{ color: THEME.textSecondary, fontSize: "13px", fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.5px" }}>
            {title}
          </Text>
        </div>
        <div style={{ display: "flex", alignItems: "baseline", gap: "8px" }}>
          <Title level={2} style={{ color: THEME.text, margin: 0, fontWeight: 600 }}>
            {value}
          </Title>
          {suffix && <Text style={{ color: THEME.textSecondary, fontSize: "14px" }}>{suffix}</Text>}
        </div>
        {trend && (
          <div style={{ marginTop: "8px", display: "flex", alignItems: "center", gap: "4px" }}>
            {trend.isUp ? <ArrowUpOutlined style={{ color: THEME.success }} /> : <ArrowDownOutlined style={{ color: THEME.danger }} />}
            <Text style={{ color: trend.isUp ? THEME.success : THEME.danger, fontSize: "12px" }}>
              {Math.abs(trend.value)}%
            </Text>
            <Text style={{ color: THEME.textTertiary, fontSize: "11px" }}>vs last hour</Text>
          </div>
        )}
      </div>
    </Card>
  );
};

// Section header
const SectionHeader: React.FC<{ title: string; subtitle?: string; icon?: React.ReactNode }> = ({
  title,
  subtitle,
  icon,
}) => (
  <div style={{ marginBottom: "24px" }}>
    <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: subtitle ? "8px" : 0 }}>
      {icon && <div style={{ color: THEME.accent, fontSize: "24px" }}>{icon}</div>}
      <Title level={3} style={{ color: THEME.text, margin: 0, fontWeight: 600 }}>
        {title}
      </Title>
    </div>
    {subtitle && <Text style={{ color: THEME.textSecondary, fontSize: "14px" }}>{subtitle}</Text>}
  </div>
);

export const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const { lat, lng, city, state, country, loading: locationLoading } = useLocation();
  const userId = user?.id || "test_user_id";

  const locationString = city && state ? `${city}, ${state}` : city ? city : country || `${lat},${lng}`;

  const {
    data: energyResponse,
    loading: energyLoading,
    error: energyError,
  } = useQuery(GET_CURRENT_ENERGY_DATA, {
    variables: { userId },
    skip: !userId || locationLoading,
  });

  const {
    data: weatherResponse,
    loading: weatherLoading,
    error: weatherError,
  } = useQuery(GET_CURRENT_WEATHER_DATA, {
    variables: { location: locationString },
    skip: locationLoading,
  });

  if (energyLoading || weatherLoading || locationLoading) {
    return (
      <div style={{ background: THEME.bg, minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Spin size="large" style={{ color: THEME.accent }} tip={<Text style={{ color: THEME.textSecondary }}>Loading real-time data...</Text>} />
      </div>
    );
  }

  if (energyError) {
    return (
      <div style={{ background: THEME.bg, minHeight: "100vh", padding: "24px" }}>
        <Alert
          message="Error"
          description={`Error loading energy data: ${energyError.message}`}
          type="error"
          showIcon
        />
      </div>
    );
  }

  if (weatherError) {
    return (
      <div style={{ background: THEME.bg, minHeight: "100vh", padding: "24px" }}>
        <Alert
          message="Error"
          description={`Error loading weather data: ${weatherError.message}`}
          type="error"
          showIcon
        />
      </div>
    );
  }

  const currentEnergy = energyResponse?.energyData;
  const currentWeather = weatherResponse?.weatherData;

  return (
    <div style={{ background: THEME.bg, minHeight: "100vh" }}>
      <Content style={{ padding: "0", width: "100%" }}>
        <div style={{ maxWidth: "1600px", margin: "0 auto", padding: "48px 24px" }}>

          {/* Hero Section */}
          <AnimatedSection>
            <div style={{ marginBottom: "64px" }}>
              <Title level={1} style={{ color: THEME.text, marginBottom: "16px", fontWeight: 700, fontSize: "48px" }}>
                Command Center
              </Title>
              <Text style={{ color: THEME.textSecondary, fontSize: "16px" }}>
                Real-time energy intelligence and environmental monitoring
              </Text>
            </div>
          </AnimatedSection>

          {/* Primary Metrics */}
          <AnimatedSection delay={100}>
            <SectionHeader title="Energy System" subtitle="Live performance metrics" icon={<ThunderboltOutlined />} />
            <Row gutter={[16, 16]} style={{ marginBottom: "64px" }}>
              <Col xs={24} sm={12} lg={6}>
                <MetricCard
                  title="Solar Output"
                  value={currentEnergy?.solarOutput ?? "—"}
                  suffix="kW"
                  icon={<ThunderboltOutlined />}
                  color={THEME.success}
                  trend={{ value: 12.5, isUp: true }}
                />
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <MetricCard
                  title="Wind Output"
                  value={currentEnergy?.windOutput ?? "—"}
                  suffix="kW"
                  icon={<SyncOutlined />}
                  color={THEME.accent}
                  trend={{ value: 8.2, isUp: true }}
                />
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <MetricCard
                  title="Battery Level"
                  value={currentEnergy?.batteryLevel ?? "—"}
                  suffix="%"
                  icon={<PoweroffOutlined />}
                  color={THEME.purple}
                />
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <MetricCard
                  title="Grid Status"
                  value={currentEnergy?.gridStatus || "NEUTRAL"}
                  icon={<EnvironmentOutlined />}
                  color={THEME.warning}
                />
              </Col>
            </Row>
          </AnimatedSection>

          {/* Environmental Conditions */}
          <AnimatedSection delay={200}>
            <SectionHeader title="Environmental Conditions" subtitle="Current weather and climate data" icon={<CloudOutlined />} />
            <Row gutter={[16, 16]} style={{ marginBottom: "64px" }}>
              <Col xs={24} sm={12} lg={6}>
                <MetricCard
                  title="Temperature"
                  value={currentWeather?.temperature ?? "—"}
                  suffix="°C"
                  icon={<BarChartOutlined />}
                  color="#ff6b6b"
                />
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <MetricCard
                  title="Humidity"
                  value={currentWeather?.humidity ?? "—"}
                  suffix="%"
                  icon={<CloudOutlined />}
                  color={THEME.accent}
                />
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <MetricCard
                  title="Solar Irradiance"
                  value={currentWeather?.solarIrradiance ?? "—"}
                  suffix="W/m²"
                  icon={<RiseOutlined />}
                  color={THEME.warning}
                />
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <MetricCard
                  title="Wind Speed"
                  value={currentWeather?.windSpeed ?? "—"}
                  suffix="m/s"
                  icon={<SyncOutlined />}
                  color={THEME.success}
                />
              </Col>
            </Row>
          </AnimatedSection>

          {/* Carbon Impact */}
          <AnimatedSection delay={300}>
            <SectionHeader title="Environmental Impact" subtitle="Carbon footprint and sustainability metrics" icon={<EnvironmentOutlined />} />
            <Row gutter={[16, 16]} style={{ marginBottom: "64px" }}>
              <Col xs={24} lg={12}>
                <Card
                  style={{
                    background: THEME.surface,
                    border: `1px solid ${THEME.border}`,
                    borderRadius: "12px",
                    height: "100%",
                  }}
                >
                  <div style={{ padding: "8px 0" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "24px" }}>
                      <div>
                        <Text style={{ color: THEME.textSecondary, fontSize: "13px", fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.5px" }}>
                          Carbon Offset
                        </Text>
                        <Title level={3} style={{ color: THEME.text, margin: "8px 0 0 0", fontWeight: 600 }}>
                          2,450 kg
                        </Title>
                      </div>
                      <EnvironmentOutlined style={{ fontSize: "32px", color: THEME.success }} />
                    </div>
                    <Progress
                      percent={78}
                      strokeColor={{
                        "0%": THEME.success,
                        "100%": THEME.accent,
                      }}
                      trailColor={THEME.border}
                      strokeWidth={8}
                    />
                    <Text style={{ color: THEME.textTertiary, fontSize: "12px", marginTop: "8px", display: "block" }}>
                      78% of monthly goal
                    </Text>
                  </div>
                </Card>
              </Col>
              <Col xs={24} lg={12}>
                <Card
                  style={{
                    background: THEME.surface,
                    border: `1px solid ${THEME.border}`,
                    borderRadius: "12px",
                    height: "100%",
                  }}
                >
                  <div style={{ padding: "8px 0" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "24px" }}>
                      <div>
                        <Text style={{ color: THEME.textSecondary, fontSize: "13px", fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.5px" }}>
                          Energy Savings
                        </Text>
                        <Title level={3} style={{ color: THEME.text, margin: "8px 0 0 0", fontWeight: 600 }}>
                          $1,240
                        </Title>
                      </div>
                      <DollarOutlined style={{ fontSize: "32px", color: THEME.warning }} />
                    </div>
                    <Progress
                      percent={65}
                      strokeColor={{
                        "0%": THEME.warning,
                        "100%": THEME.success,
                      }}
                      trailColor={THEME.border}
                      strokeWidth={8}
                    />
                    <Text style={{ color: THEME.textTertiary, fontSize: "12px", marginTop: "8px", display: "block" }}>
                      65% increase vs last month
                    </Text>
                  </div>
                </Card>
              </Col>
            </Row>
          </AnimatedSection>

          {/* System Health */}
          <AnimatedSection delay={400}>
            <SectionHeader title="System Health" subtitle="Performance and reliability metrics" icon={<FireOutlined />} />
            <Row gutter={[16, 16]}>
              <Col xs={24} lg={12}>
                <Card
                  style={{
                    background: THEME.surface,
                    border: `1px solid ${THEME.border}`,
                    borderRadius: "12px",
                    height: "100%",
                  }}
                >
                  <div style={{ padding: "8px 0" }}>
                    <Text style={{ color: THEME.textSecondary, fontSize: "13px", fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.5px", display: "block", marginBottom: "16px" }}>
                      Active Systems
                    </Text>
                    <List
                      itemLayout="horizontal"
                      dataSource={[
                        { name: "Solar Array Alpha", status: "operational", efficiency: 94 },
                        { name: "Wind Turbine Beta", status: "operational", efficiency: 87 },
                        { name: "Battery Storage", status: "charging", efficiency: 92 },
                        { name: "Grid Interface", status: "operational", efficiency: 98 },
                      ]}
                      renderItem={(item) => (
                        <List.Item style={{ borderBottom: `1px solid ${THEME.borderSubtle}`, padding: "12px 0" }}>
                          <List.Item.Meta
                            avatar={<div style={{ width: "8px", height: "8px", borderRadius: "50%", background: THEME.success, boxShadow: `0 0 8px ${THEME.success}` }} />}
                            title={<Text style={{ color: THEME.text, fontSize: "14px" }}>{item.name}</Text>}
                            description={
                              <div style={{ display: "flex", alignItems: "center", gap: "12px", marginTop: "4px" }}>
                                <Tag color="success" style={{ margin: 0, fontSize: "11px" }}>{item.status}</Tag>
                                <Text style={{ color: THEME.textTertiary, fontSize: "12px" }}>{item.efficiency}% efficiency</Text>
                              </div>
                            }
                          />
                        </List.Item>
                      )}
                    />
                  </div>
                </Card>
              </Col>
              <Col xs={24} lg={12}>
                <Card
                  style={{
                    background: THEME.surface,
                    border: `1px solid ${THEME.border}`,
                    borderRadius: "12px",
                    height: "100%",
                  }}
                >
                  <div style={{ padding: "8px 0" }}>
                    <Text style={{ color: THEME.textSecondary, fontSize: "13px", fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.5px", display: "block", marginBottom: "16px" }}>
                      Recent Alerts
                    </Text>
                    <List
                      itemLayout="horizontal"
                      dataSource={[
                        { type: "info", message: "Solar output peak reached", time: "2 min ago" },
                        { type: "success", message: "Battery fully charged", time: "15 min ago" },
                        { type: "warning", message: "High wind speeds detected", time: "1 hour ago" },
                      ]}
                      renderItem={(item) => (
                        <List.Item style={{ borderBottom: `1px solid ${THEME.borderSubtle}`, padding: "12px 0" }}>
                          <List.Item.Meta
                            avatar={<div style={{ width: "8px", height: "8px", borderRadius: "50%", background: item.type === "warning" ? THEME.warning : item.type === "success" ? THEME.success : THEME.accent }} />}
                            title={<Text style={{ color: THEME.text, fontSize: "14px" }}>{item.message}</Text>}
                            description={<Text style={{ color: THEME.textTertiary, fontSize: "12px" }}>{item.time}</Text>}
                          />
                        </List.Item>
                      )}
                    />
                  </div>
                </Card>
              </Col>
            </Row>
          </AnimatedSection>

        </div>
      </Content>
    </div>
  );
};

export default Dashboard;